// CommandPalette (design-system §4.29, §3.5): 640 px breit im oberen Drittel, `overlay` mit `shadow-lg`.
// Rein darstellend: Treffer liefern die übergebenen Anbieter (Seiten, Datensätze, Befehle); die Hülle verdrahtet sie.
import { Dialog as BaseDialog } from '@base-ui/react/dialog'
import { Command } from 'cmdk'
import { CornerDownLeft, Search, Sparkles, type LucideIcon } from 'lucide-react'
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { cn } from '../lib/cn'
import type { Hotkey } from '../lib/hotkeys'
import { useScanInput, type ScanResult } from '../lib/scan'
import { LayerProvider, useLayer, Z } from './context'
import { Kbd } from './Kbd'
import { Spinner } from './Spinner'

export interface CommandItem {
  id: string
  kind: 'page' | 'record' | 'command' | 'setting' | 'assistant'
  title: string
  subtitle?: string
  icon?: LucideIcon
  /** Nummer in Mono vor dem Titel */
  code?: string
  /** rechts: Bestand, Status, Betrag */
  meta?: ReactNode
  hotkey?: Hotkey
  keywords?: string[]
  perform: () => void | Promise<void>
  /** Tab: Aktionen zu diesem Treffer */
  actions?: CommandItem[]
  disabledReason?: string
}

export interface CommandSearchContext {
  signal: AbortSignal
  locationId: string | null
  route: string
}

export interface CommandProvider {
  id: string
  /** „Artikel“, „Belege“, „Befehle“ */
  heading: string
  prefix?: '>' | '#' | '@' | ':' | '?'
  /** kleinere Zahl = weiter oben */
  priority?: number
  minChars?: number
  search: (query: string, ctx: CommandSearchContext) => CommandItem[] | Promise<CommandItem[]>
}

export interface CommandPaletteProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  providers: CommandProvider[]
  recent?: CommandItem[]
  /** „Suchen oder Befehl eingeben …“ */
  placeholder?: string
  initialQuery?: string
  /** fehlt → kein Eintrag „KI-Assistent“ */
  onAskAssistant?: (query: string) => void
  onScan?: (result: ScanResult) => void
  /** Arbeitskontext für die Anbieter */
  locationId?: string | null
  route?: string
}

const normalize = (text: string): string => text.toLocaleLowerCase('de').normalize('NFD').replace(/[̀-ͯ]/g, '')

/** Treffergüte eines Eintrags: Anfang des Titels vor Wortanfang vor Fundstelle; 0 = kein Treffer. */
export function scoreCommand(item: CommandItem, query: string): number {
  const needle = normalize(query.trim())
  if (needle === '') return 1
  const title = normalize(item.title)
  if (title.startsWith(needle)) return 100
  if (item.code && normalize(item.code).startsWith(needle)) return 95
  if (title.split(/[\s/–-]+/).some((word) => word.startsWith(needle))) return 80
  if (title.includes(needle)) return 60
  if (item.keywords?.some((keyword) => normalize(keyword).startsWith(needle))) return 50
  if (item.subtitle && normalize(item.subtitle).includes(needle)) return 30
  return 0
}

/** Anbieter aus einer festen Liste (Seiten, Befehle, Einstellungen) – sucht im Browser. */
export function staticProvider(
  options: Omit<CommandProvider, 'search'> & { items: CommandItem[]; limit?: number },
): CommandProvider {
  const { items, limit = 8, ...provider } = options
  return {
    ...provider,
    search: (query) =>
      items
        .map((item) => ({ item, score: scoreCommand(item, query) }))
        .filter((entry) => entry.score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, limit)
        .map((entry) => entry.item),
  }
}

interface Group {
  id: string
  heading: string
  items: CommandItem[]
  loading?: boolean
}

const itemClass =
  'flex min-h-9 cursor-default items-center gap-2.5 rounded-sm px-2.5 py-1.5 text-dense outline-none select-none touch:min-h-12 touch:text-touch data-[disabled=true]:text-fg-disabled data-[selected=true]:bg-overlay-hover'

function Row({ item }: { item: CommandItem }) {
  const Icon = item.icon
  return (
    <>
      {Icon && <Icon className="size-4 shrink-0 text-fg-muted touch:size-5" strokeWidth={1.75} aria-hidden />}
      {item.code && <span className="code-id shrink-0 text-fg-muted">{item.code}</span>}
      <span className="min-w-0 flex-1">
        <span className="block truncate-line">{item.title}</span>
        {(item.subtitle || item.disabledReason) && (
          <span className="block truncate-line text-caption text-fg-subtle">
            {item.disabledReason ?? item.subtitle}
          </span>
        )}
      </span>
      {item.meta !== undefined && <span className="shrink-0 text-caption text-fg-muted">{item.meta}</span>}
      {item.hotkey && <Kbd hotkey={item.hotkey} className="hidden md:inline-flex touch:hidden" />}
    </>
  )
}

/** Suchen und Befehle an einer Stelle: ⌘K / Strg+K. Erreicht jede Seite, jeden Datensatz und jeden Befehl. */
export function CommandPalette({
  open,
  onOpenChange,
  providers,
  recent,
  placeholder = 'Suchen oder Befehl eingeben …',
  initialQuery = '',
  onAskAssistant,
  onScan,
  locationId = null,
  route = '',
}: CommandPaletteProps) {
  const zIndex = useLayer(Z.palette)
  const [query, setQuery] = useState(initialQuery)
  const [results, setResults] = useState<Record<string, CommandItem[]>>({})
  const [loadingIds, setLoadingIds] = useState<ReadonlySet<string>>(new Set())
  const [selected, setSelected] = useState('')
  const [parent, setParent] = useState<CommandItem | null>(null)
  const [busy, setBusy] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const providersRef = useRef(providers)
  providersRef.current = providers

  // Die Markierung folgt dem ersten Treffer, bis der Benutzer sie selbst bewegt
  const moved = useRef(false)

  useEffect(() => {
    if (!open) return
    setQuery(initialQuery)
    setParent(null)
    setSelected('')
    // auch wenn der Suchtext derselbe bleibt (die Hülle setzt initialQuery nach einem Scan auf den schon
    // getippten Code): Die Markierung beginnt wieder beim ersten Treffer
    moved.current = false
  }, [open, initialQuery])

  useScanInput((result) => onScan?.(result), { enabled: open && Boolean(onScan), captureInInputs: true })

  // Suche: lokale Treffer sofort, Server-Treffer wachsen gruppenweise nach
  useEffect(() => {
    if (!open || parent) return
    const controller = new AbortController()
    const text = query.trimStart()
    const prefixed = providersRef.current.filter((provider) => provider.prefix && text.startsWith(provider.prefix))
    const active = prefixed.length > 0 ? prefixed : providersRef.current
    const term = prefixed.length > 0 ? text.slice(1).trim() : text.trim()
    const next: Record<string, CommandItem[]> = {}
    const waiting = new Set<string>()
    for (const provider of active) {
      if (term.length < (provider.minChars ?? 0)) continue
      let found: CommandItem[] | Promise<CommandItem[]>
      try {
        found = provider.search(term, { signal: controller.signal, locationId, route })
      } catch {
        continue
      }
      if (found instanceof Promise) {
        waiting.add(provider.id)
        found.then(
          (items) => {
            if (controller.signal.aborted) return
            setResults((current) => ({ ...current, [provider.id]: items }))
            setLoadingIds((current) => new Set([...current].filter((id) => id !== provider.id)))
          },
          () => {
            if (controller.signal.aborted) return
            setLoadingIds((current) => new Set([...current].filter((id) => id !== provider.id)))
          },
        )
      } else next[provider.id] = found
    }
    // bisherige Server-Treffer bleiben stehen, bis die neuen da sind
    setResults((current) => ({ ...Object.fromEntries([...waiting].map((id) => [id, current[id] ?? []])), ...next }))
    setLoadingIds(waiting)
    return () => controller.abort()
  }, [open, query, parent, locationId, route])

  const groups = useMemo<Group[]>(() => {
    if (parent) return [{ id: 'actions', heading: `Aktionen zu „${parent.title}“`, items: parent.actions ?? [] }]
    const list: Group[] = []
    if (query.trim() === '' && recent && recent.length > 0)
      list.push({ id: 'recent', heading: 'Zuletzt verwendet', items: recent })
    for (const provider of [...providers].sort((a, b) => (a.priority ?? 50) - (b.priority ?? 50))) {
      const items = results[provider.id]
      if ((items && items.length > 0) || loadingIds.has(provider.id))
        list.push({
          id: provider.id,
          heading: provider.heading,
          items: items ?? [],
          loading: loadingIds.has(provider.id),
        })
    }
    return list
  }, [parent, providers, results, loadingIds, recent, query])

  const byValue = useMemo(() => {
    const map = new Map<string, CommandItem>()
    for (const group of groups) for (const item of group.items) map.set(`${group.id}/${item.id}`, item)
    return map
  }, [groups])

  const firstValue = byValue.keys().next().value as string | undefined
  useEffect(() => {
    moved.current = false
  }, [query, parent])
  // hängt auch an `selected`: Wird die Markierung geleert, ohne dass sich der erste Treffer ändert (nur
  // initialQuery neu gesetzt), markiert sonst niemand neu – und Enter täte nichts
  useEffect(() => {
    if (!moved.current && firstValue !== undefined && selected !== firstValue) setSelected(firstValue)
  }, [firstValue, selected])

  const run = async (item: CommandItem): Promise<void> => {
    if (item.disabledReason || busy) return
    setBusy(true)
    try {
      await item.perform()
      onOpenChange(false)
    } finally {
      setBusy(false)
    }
  }

  const assistantQuery = query.trim()
  const hasItems = groups.some((group) => group.items.length > 0)
  const loading = loadingIds.size > 0

  return (
    <BaseDialog.Root open={open} onOpenChange={(next) => onOpenChange(next)}>
      <BaseDialog.Portal>
        {/* kein Abdunkeln der ganzen Seite – nur ein leichter Schleier */}
        <BaseDialog.Backdrop
          className="fixed inset-0 bg-black/20 transition-opacity duration-(--duration-fast) data-ending-style:opacity-0 data-starting-style:opacity-0"
          style={{ zIndex }}
        />
        <BaseDialog.Viewport
          className="fixed inset-0 flex items-start justify-center px-3 pt-[12vh] max-md:pt-3"
          style={{ zIndex }}
        >
          <BaseDialog.Popup
            initialFocus={inputRef}
            aria-label="Befehlspalette"
            data-ui="command-palette"
            className={cn(
              'flex max-h-[min(32rem,76vh)] w-full max-w-160 flex-col overflow-hidden rounded-lg border border-line bg-overlay text-fg shadow-lg outline-none',
              'transition-[opacity,scale] duration-(--duration-fast) ease-out data-ending-style:opacity-0 data-starting-style:scale-98 data-starting-style:opacity-0',
            )}
          >
            <LayerProvider z={zIndex}>
              <BaseDialog.Title className="sr-only">Befehlspalette</BaseDialog.Title>
              <Command
                label="Befehlspalette"
                shouldFilter={false}
                loop
                value={selected}
                onValueChange={setSelected}
                className="flex min-h-0 flex-1 flex-col"
                onKeyDown={(event) => {
                  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') moved.current = true
                  const current = byValue.get(selected)
                  if (event.key === 'Tab' && !parent && current?.actions && current.actions.length > 0) {
                    event.preventDefault()
                    setParent(current)
                    setQuery('')
                  } else if (parent && ((event.key === 'Backspace' && query === '') || event.key === 'Escape')) {
                    event.preventDefault()
                    event.stopPropagation()
                    setParent(null)
                  }
                }}
              >
                <div className="flex h-12 shrink-0 items-center gap-2.5 border-b border-line px-3.5 touch:h-14">
                  {loading || busy ? (
                    <Spinner className="size-4 text-fg-subtle" />
                  ) : (
                    <Search className="size-4 shrink-0 text-fg-subtle" strokeWidth={1.75} aria-hidden />
                  )}
                  <Command.Input
                    ref={inputRef}
                    value={query}
                    onValueChange={setQuery}
                    placeholder={parent ? 'Aktion wählen …' : placeholder}
                    className="h-full min-w-0 flex-1 bg-transparent text-body outline-none placeholder:text-fg-subtle touch:text-touch"
                  />
                  <Kbd hotkey="esc" className="hidden md:inline-flex" />
                </div>
                <Command.List
                  onPointerMove={() => (moved.current = true)}
                  className="min-h-0 flex-1 overflow-y-auto p-1.5 [&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:text-micro [&_[cmdk-group-heading]]:text-fg-subtle [&_[cmdk-group-heading]]:uppercase"
                >
                  {!hasItems && !loading && !(onAskAssistant && assistantQuery !== '') && (
                    <Command.Empty className="px-2.5 py-6 text-dense text-fg-subtle">
                      {assistantQuery === ''
                        ? 'Tippen Sie, um Seiten, Artikel, Belege und Befehle zu finden.'
                        : `Keine Treffer für „${assistantQuery}“.`}
                    </Command.Empty>
                  )}
                  {groups.map((group) => (
                    <Command.Group key={group.id} heading={group.heading}>
                      {group.items.map((item) => (
                        <Command.Item
                          key={item.id}
                          value={`${group.id}/${item.id}`}
                          disabled={Boolean(item.disabledReason)}
                          onSelect={() => void run(item)}
                          className={itemClass}
                        >
                          <Row item={item} />
                        </Command.Item>
                      ))}
                      {group.loading && group.items.length === 0 && (
                        <div className="px-2.5 py-1.5 text-dense text-fg-subtle">Wird gesucht …</div>
                      )}
                    </Command.Group>
                  ))}
                  {onAskAssistant && !parent && assistantQuery !== '' && (
                    <Command.Group heading="KI-Assistent">
                      <Command.Item
                        value="assistant/ask"
                        onSelect={() => {
                          onAskAssistant(assistantQuery)
                          onOpenChange(false)
                        }}
                        className={itemClass}
                      >
                        <Sparkles className="size-4 shrink-0 text-fg-muted" strokeWidth={1.75} aria-hidden />
                        <span className="min-w-0 flex-1 truncate-line">
                          KI-Assistent fragen: <span className="font-medium">„{assistantQuery}“</span>
                        </span>
                      </Command.Item>
                    </Command.Group>
                  )}
                </Command.List>
                <div className="hidden shrink-0 items-center gap-4 border-t border-line bg-sunken/50 px-3.5 py-2 text-caption text-fg-subtle md:flex touch:hidden">
                  <span className="flex items-center gap-1.5">
                    <CornerDownLeft className="size-3.5" strokeWidth={1.75} aria-hidden /> Öffnen
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Kbd hotkey="up" /> <Kbd hotkey="down" /> Wechseln
                  </span>
                  {byValue.get(selected)?.actions?.length ? (
                    <span className="flex items-center gap-1.5">
                      <Kbd hotkey="tab" /> Aktionen
                    </span>
                  ) : null}
                  <span className="ml-auto">
                    {providers
                      .filter((provider) => provider.prefix)
                      .map((provider) => `${provider.prefix} ${provider.heading}`)
                      .join(' · ')}
                  </span>
                </div>
              </Command>
            </LayerProvider>
          </BaseDialog.Popup>
        </BaseDialog.Viewport>
      </BaseDialog.Portal>
    </BaseDialog.Root>
  )
}
